import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { usePlanos, useCriarPlano, useAtualizarPlano } from "@/hooks/usePlanos";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Eye } from "lucide-react";

export const Planos = () => {
  const { data: planos, isLoading } = usePlanos();
  const criarPlano = useCriarPlano();
  const atualizarPlano = useAtualizarPlano();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlano, setEditingPlano] = useState<any>(null);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    valor_por_painel: '',
    vigencia_meses: '12',
    cobertura_detalhes: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const planoData = {
        nome: formData.nome,
        descricao: formData.descricao || undefined,
        valor_por_painel: formData.valor_por_painel ? parseFloat(formData.valor_por_painel) : undefined,
        vigencia_meses: parseInt(formData.vigencia_meses),
        cobertura_detalhes: formData.cobertura_detalhes ? JSON.parse(formData.cobertura_detalhes) : undefined
      };

      if (editingPlano) {
        await atualizarPlano.mutateAsync({ id: editingPlano.id, ...planoData });
        toast({ title: "Plano atualizado com sucesso!" });
      } else {
        await criarPlano.mutateAsync(planoData);
        toast({ title: "Plano criado com sucesso!" });
      }

      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast({ 
        title: "Erro ao salvar plano", 
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive" 
      });
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      descricao: '',
      valor_por_painel: '',
      vigencia_meses: '12',
      cobertura_detalhes: ''
    });
    setEditingPlano(null);
  };

  const handleEdit = (plano: any) => {
    setEditingPlano(plano);
    setFormData({
      nome: plano.nome,
      descricao: plano.descricao || '',
      valor_por_painel: plano.valor_por_painel?.toString() || '',
      vigencia_meses: plano.vigencia_meses?.toString() || '12',
      cobertura_detalhes: plano.cobertura_detalhes ? JSON.stringify(plano.cobertura_detalhes, null, 2) : ''
    });
    setDialogOpen(true);
  };

  if (isLoading) {
    return <div className="p-6">Carregando planos...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Planos de Seguro</h1>
          <p className="text-gray-500">Gerencie os planos de seguro fotovoltaico</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Plano
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingPlano ? 'Editar Plano' : 'Novo Plano de Seguro'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="nome">Nome do Plano</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  required
                />
              </div>

              <div>
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="valor_por_painel">Valor por Painel (R$)</Label>
                <Input
                  id="valor_por_painel"
                  type="number"
                  step="0.01"
                  value={formData.valor_por_painel}
                  onChange={(e) => setFormData(prev => ({ ...prev, valor_por_painel: e.target.value }))}
                  placeholder="Ex: 15.00"
                />
              </div>

              <div>
                <Label htmlFor="vigencia_meses">Vigência (meses)</Label>
                <Input
                  id="vigencia_meses"
                  type="number"
                  value={formData.vigencia_meses}
                  onChange={(e) => setFormData(prev => ({ ...prev, vigencia_meses: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="cobertura_detalhes">Detalhes da Cobertura (JSON)</Label>
                <Textarea
                  id="cobertura_detalhes"
                  value={formData.cobertura_detalhes}
                  onChange={(e) => setFormData(prev => ({ ...prev, cobertura_detalhes: e.target.value }))}
                  placeholder='{"cobertura": ["Danos elétricos", "Roubo/Furto"], "franquia": 500}'
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={criarPlano.isPending || atualizarPlano.isPending}>
                  {editingPlano ? 'Atualizar' : 'Criar'} Plano
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {planos?.map((plano) => (
          <Card key={plano.id} className="relative">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{plano.nome}</CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(plano)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {plano.descricao && (
                <p className="text-sm text-gray-600">{plano.descricao}</p>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Valor por Painel:</span>
                <span className="font-medium">
                  {plano.valor_por_painel ? `R$ ${plano.valor_por_painel.toFixed(2)}` : 'N/A'}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Vigência:</span>
                <Badge variant="secondary">
                  {plano.vigencia_meses} meses
                </Badge>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Status:</span>
                <div className="flex items-center gap-2">
                  <Badge variant={plano.ativo ? "default" : "secondary"}>
                    {plano.ativo ? 'Ativo' : 'Inativo'}
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => atualizarPlano.mutate({ id: plano.id, ativo: !plano.ativo })}
                    disabled={atualizarPlano.isPending}
                  >
                    {plano.ativo ? 'Inativar' : 'Ativar'}
                  </Button>
                </div>
              </div>

              {plano.cobertura_detalhes && (
                <div className="pt-2 border-t">
                  <span className="text-sm text-gray-500">Cobertura:</span>
                  <div className="mt-1 text-xs">
                    {plano.cobertura_detalhes.cobertura?.map((item: string, index: number) => (
                      <Badge key={index} variant="outline" className="mr-1 mb-1">
                        {item}
                      </Badge>
                    ))}
                  </div>
                  {plano.cobertura_detalhes.franquia && (
                    <p className="text-xs text-gray-500 mt-1">
                      Franquia: R$ {plano.cobertura_detalhes.franquia}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};